import 'intersection-observer';

import { useThrottleFn } from 'ahooks';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';

import canUse from '../../utils/canUse';
import { RAF } from '../../utils/raf';
import DefaultLoading from './DefaultLoading';
import PullToRefresh from './PullToRefresh';

// Get the first visible element of the visible area
const getFirstVisibleEle = (domArr: Element[]): false | Element => {
  if (domArr.length === 0) return false;
  const binarySearch = (
    arr: Element[],
    start?: number,
    end?: number,
  ): false | Element => {
    if (end == undefined) {
      end = arr.length - 1;
    }
    if (start == undefined) {
      start = 0;
    }
    if (start >= end) {
      return false;
    }
    const m = Math.floor((start + end) / 2);
    const { top, bottom } = arr[m].getBoundingClientRect();
    if (top <= 0 && bottom >= 0) {
      return arr[m];
    }
    if (top > 0) {
      return binarySearch(arr, start, m - 1);
    } else {
      return binarySearch(arr, m + 1, end);
    }
  };
  return binarySearch(domArr);
};

interface IScrollViewProps {
  // Turn on monitoring Scroll to top
  enableOnScrollToTop?: boolean;
  // Top hook
  onScrollToTop?: (cb: VoidFunction) => void;
  // Top hook loading
  ScrollToTopLoading?: React.FC;

  // Turn on monitoring drop-down refresh
  enableOnPullToRefresh?: boolean;
  // Listen, pull down refresh events
  onPullToRefresh?: AnyAsyncFunction;
  // Pull down refresh loading
  PullToRefreshLoading?: React.FC;

  // Turn on monitoring Scroll to top
  enableOnScrollToBottom?: boolean;
  // Listen, scroll to the bottom
  onScrollToBottom?: AnyAsyncFunction;
  // Top hook loading
  ScrollToBottomLoading?: React.FC;

  // Listen, scroll events
  onScroll?: AnyAsyncFunction;
  // Listening, scrolling events, cutoff intervals
  onScrollThrottleWait?: number;

  onScrollStart: AnyAsyncFunction;

  children?: React.ReactNode;
}

export interface IScrollViewRef {
  scrollIntoView: typeof scrollIntoView;
  scrollToTop: (smooth?: boolean) => void;
  scrollToBottom: (smooth?: boolean) => void;
  checkScrolledToBottom: (distance?: number) => boolean;
}

export type scrollViewHook =
  | 'onScrollToTop'
  | 'onScrollToBottom'
  | 'onScroll'
  | 'onPullToRefresh';

// IntersectionObserver Minimum Intersection Range
const MIN_THRESHOLD = 0.8;

export const ScrollView = React.forwardRef<IScrollViewRef, IScrollViewProps>(
  (
    {
      enableOnScrollToTop = true,
      onScrollToTop,
      ScrollToTopLoading = DefaultLoading,
      enableOnScrollToBottom = true,
      onScrollToBottom,
      ScrollToBottomLoading = DefaultLoading,
      onScroll,
      onScrollThrottleWait = 60,
      enableOnPullToRefresh = true,
      onPullToRefresh,
      PullToRefreshLoading = DefaultLoading,
      children,
      onScrollStart,
    },
    ref,
  ) => {
    const scrollViewRef = useRef<HTMLDivElement>(null);
    const scrollContentRef = useRef<HTMLDivElement>(null);
    /** 当前消息长度 */
    const listLength = useRef(React.Children.toArray(children)?.length || 0);
    /** 可自由滚动的消息长度缓存 */
    const cacheLengthRef = useRef(0);

    // Scroll area top anchor
    const topAnchorRef = useRef<HTMLDivElement>(null);

    // Scroll area bottom anchor
    const bottomAnchorRef = useRef<HTMLDivElement>(null);
    // A visible area nail used to fix the visible area
    const visiblePinRef = useRef<{
      el: Element | null; // The first element in the visible area
      theDistanceToTheTop: number; // Distance from the bottom of the element to the top of the viewport
      isScrollingToBottom: boolean; // Is it scrolling to the bottom?
    }>({ el: null, theDistanceToTheTop: 0, isScrollingToBottom: false });

    const [_enableOnScrollToTop, setEnableOnScrollToTop] = useState(false);
    const [_enableOnPullToRefresh, setEnableOnPullToRefresh] = useState(false);
    const [_enableOnScrollToBottom, setEnableOnScrollToBottom] =
      useState(false);

    // Hooks that no longer need to be triggered, and accompanying functions
    const disabledHook = useRef({
      onScrollToTop: false,
      onScrollToBottom: false,
      onScroll: false,
      onPullToRefresh: false,
    });
    const handleDisableHook = (hook: scrollViewHook) => {
      disabledHook.current[hook] = true;
      if ('onScrollToTop' === hook) setEnableOnScrollToTop(false);
      else if ('onScrollToBottom' === hook) setEnableOnScrollToBottom(false);
      else if ('onPullToRefresh' === hook) setEnableOnPullToRefresh(false);
    };

    // Lock some actions to prevent them from being triggered again during operation
    const actionLock = useRef<Record<string, Promise<void> | null>>({});
    const handleActionLock = async (
      action: string,
      handler: AnyAsyncFunction,
    ) => {
      if (actionLock.current[action]) return await actionLock.current[action];
      actionLock.current[action] = handler();
      const res = await actionLock.current[action];
      actionLock.current[action] = null;
      return res;
    };

    // Scroll to the bottom
    const scrollToBottom = (smooth?: boolean) => {
      if (!bottomAnchorRef.current) return;
      if (smooth) {
        visiblePinRef.current.isScrollingToBottom = true;
        return scrollIntoView(bottomAnchorRef.current, {
          behavior: 'smooth',
        }).finally(() => {
          if (checkScrolledToBottom(10)) {
            visiblePinRef.current.isScrollingToBottom = false;
          }
          if (visiblePinRef.current.isScrollingToBottom) {
            scrollToBottom(smooth);
          }
        });
      } else {
        bottomAnchorRef.current.scrollIntoView();
      }
    };

    // Scroll to top
    const scrollToTop = (smooth?: boolean) => {
      if (!topAnchorRef.current) return;

      if (smooth) {
        scrollIntoView(topAnchorRef.current, { behavior: 'smooth' });
      } else {
        topAnchorRef.current.scrollIntoView();
      }
    };

    // Check if the scroll area can be scrolled
    const checkCanScroll = () => {
      if (!scrollViewRef.current) return false;
      return (
        scrollViewRef.current.scrollHeight > scrollViewRef.current.clientHeight
      );
    };

    // Check if it has scrolled to the bottom of the page
    const checkScrolledToBottom = (distance = 0) => {
      if (!scrollViewRef.current) return true;
      return (
        scrollViewRef.current.scrollHeight -
          (scrollViewRef.current.scrollTop +
            scrollViewRef.current.clientHeight) <=
        distance
      );
    };

    // Top handling function
    const handleOnScrollToTop = useCallback(
      () =>
        handleActionLock('handleOnScrollToTop', async () => {
          if (typeof onScrollToTop === 'function') {
            try {
              await onScrollToTop(() => {
                handleDisableHook('onScrollToTop');
                if (onScrollToTop === onPullToRefresh) {
                  handleDisableHook('onPullToRefresh');
                }
              });
            } catch (e) {}
          }
        }),
      [onScrollToTop, onPullToRefresh],
    );

    // Drop-down refresh handler
    const handleOnPullToRefresh = useCallback(
      () =>
        handleActionLock('handleOnPullToRefresh', async () => {
          if (typeof onPullToRefresh === 'function') {
            try {
              await onPullToRefresh(() => {
                handleDisableHook('onPullToRefresh');
                if (onPullToRefresh === onScrollToTop) {
                  handleDisableHook('onScrollToTop');
                }
              });
            } catch (e) {}
          }
        }),
      [onPullToRefresh, onScrollToTop],
    );

    // Bottom handler function
    const handleOnScrollToBottom = useCallback(
      () =>
        handleActionLock('handleOnScrollToBottom', async () => {
          if (typeof onScrollToBottom === 'function') {
            await onScrollToBottom(() => {
              handleDisableHook('onScrollToBottom');
            });
          }
        }),
      [onScrollToBottom],
    );

    // Scroll handler function
    const { run: handleOnScroll } = useThrottleFn(
      (e) => {
        if (typeof onScroll === 'function') {
          onScroll(e);
        }
      },
      {
        wait: onScrollThrottleWait,
        leading: true,
      },
    );

    useImperativeHandle(ref, () => {
      return {
        scrollIntoView,
        scrollToBottom,
        scrollToTop,
        checkScrolledToBottom,
      };
    });

    // If the scroll area is scrollable & & does not scroll to the bottom
    // Then the cache, the first visible element in the scroll area, and the distance from the bottom of this element to the top of the viewport
    const handleInitVisiblePinRef = () => {
      if (
        checkScrolledToBottom(10) ||
        visiblePinRef.current.isScrollingToBottom
      ) {
        visiblePinRef.current.el = null;
      } else if (
        visiblePinRef.current.el &&
        visiblePinRef.current.el ===
          getFirstVisibleEle([visiblePinRef.current.el])
      ) {
        visiblePinRef.current.el = visiblePinRef.current.el;
      } else {
        const domArr = Array.from(scrollContentRef.current?.children || []);
        visiblePinRef.current.el =
          getFirstVisibleEle(domArr) || domArr[0] || null;
      }
      if (visiblePinRef.current.el) {
        const bottomHeight =
          visiblePinRef.current.el.getBoundingClientRect().bottom;
        if (bottomHeight > 0) {
          visiblePinRef.current.theDistanceToTheTop =
            visiblePinRef.current.el.getBoundingClientRect().bottom;
        }
      } else {
        visiblePinRef.current.theDistanceToTheTop = 0;
      }
    };

    // Fixed pages to prevent page shake when content area changes
    const handlePinContentView = () => {
      if (scrollViewRef.current && visiblePinRef.current.el) {
        scrollViewRef.current.scrollTop =
          scrollViewRef.current.scrollTop +
          (visiblePinRef.current.el.getBoundingClientRect().bottom -
            visiblePinRef.current.theDistanceToTheTop);

        RAF((cancel?: VoidFunction) => {
          if (
            !scrollViewRef.current ||
            !visiblePinRef.current.el ||
            visiblePinRef.current.el.getBoundingClientRect().bottom ===
              visiblePinRef.current.theDistanceToTheTop
          ) {
            typeof cancel === 'function' && cancel();
            return;
          }

          scrollViewRef.current.scrollTop =
            scrollViewRef.current.scrollTop +
            (visiblePinRef.current.el.getBoundingClientRect().bottom -
              visiblePinRef.current.theDistanceToTheTop);
        }, 50);
      }
    };

    // Automatic positioning
    const handleAutoScrollTo = () => {
      // No positioning required if scrolling to the bottom
      if (visiblePinRef.current.isScrollingToBottom) return;
      // If you need to fix the page
      if (scrollViewRef.current && visiblePinRef.current.el) {
        /** 通过消息长度判断锁线上滚动 */
        if (cacheLengthRef.current !== listLength.current) {
          handlePinContentView();
        }
      }
      // Otherwise scroll to the bottom
      else {
        scrollToBottom(true);
      }
      cacheLengthRef.current = listLength.current;
    };

    const observerRef = useRef<IntersectionObserver>();

    // Add, Bottom & Top
    useEffect(() => {
      let enableObserver = false;
      observerRef.current = new IntersectionObserver(
        (entries, { root }) => {
          // No need to trigger hook when initializing observer
          if (!enableObserver) {
            enableObserver = true;
            return;
          }
          // If the scroll area height is less than or equal to the container height, there is no need to trigger the hook
          if (
            !(
              root &&
              root.clientHeight &&
              root.scrollHeight &&
              root.clientHeight < root.scrollHeight
            )
          ) {
            return;
          }

          entries.forEach(({ target, intersectionRatio }) => {
            if (
              _enableOnScrollToTop &&
              target === topAnchorRef.current &&
              intersectionRatio >= MIN_THRESHOLD
            ) {
              handleOnScrollToTop();
            }
            if (
              _enableOnScrollToBottom &&
              target === bottomAnchorRef.current &&
              intersectionRatio >= MIN_THRESHOLD
            ) {
              handleOnScrollToBottom();
            }
          });
        },
        {
          root: scrollViewRef.current,
          rootMargin: '0px',
          threshold: MIN_THRESHOLD,
        },
      );
      if (_enableOnScrollToTop && topAnchorRef.current) {
        observerRef.current?.observe(topAnchorRef.current);
      }
      if (_enableOnScrollToBottom && bottomAnchorRef.current) {
        observerRef.current?.observe(bottomAnchorRef.current);
      }
      return () => {
        observerRef.current?.disconnect();
      };
    }, [
      _enableOnScrollToTop,
      _enableOnScrollToBottom,
      handleOnScrollToTop,
      handleOnScrollToBottom,
    ]);

    // Monitor the height of the content area, which is used to set whether to turn on the bottom touch and top
    useEffect(() => {
      const observer = new ResizeObserver((entries) => {
        if (!Array.isArray(entries) || !entries.length) return;
        entries.forEach(({ target, contentRect }) => {
          if (target === scrollContentRef.current && scrollViewRef.current) {
            if (contentRect.height > scrollViewRef.current.clientHeight) {
              setEnableOnScrollToTop(
                !disabledHook.current['onScrollToTop'] && enableOnScrollToTop,
              );
              setEnableOnScrollToBottom(
                !disabledHook.current['onScrollToBottom'] &&
                  enableOnScrollToBottom,
              );
              setEnableOnPullToRefresh(false);
            } else {
              setEnableOnScrollToTop(false);
              setEnableOnScrollToBottom(false);
              setEnableOnPullToRefresh(
                !disabledHook.current['onPullToRefresh'] &&
                  enableOnPullToRefresh,
              );
            }
            handleAutoScrollTo();
          }
        });
      });
      if (scrollContentRef.current && scrollViewRef.current) {
        observer.observe(scrollContentRef.current);
      }
      return () => {
        observer.disconnect();
      };
    }, []);

    // Register actions to be done after dom update
    useMemo(() => {
      listLength.current = React.Children.toArray(children)?.length;
      handleInitVisiblePinRef();
    }, [children]);

    return (
      <PullToRefresh
        handler={handleOnPullToRefresh}
        disabled={!_enableOnPullToRefresh}
        Loading={PullToRefreshLoading}
      >
        <div
          onTouchStart={onScrollStart}
          ref={scrollViewRef}
          onScroll={handleOnScroll}
          className={clsx('jarvis-ScrollView')}
        >
          {/* Top */}
          <div
            ref={topAnchorRef}
            className={clsx('jarvis-ScrollView__topAnchor')}
          >
            {_enableOnScrollToTop && <ScrollToTopLoading />}
          </div>
          {/* Content area */}
          <div
            ref={scrollContentRef}
            className={clsx('jarvis-ScrollView__content', {
              'no-touch': !canUse('touch'),
            })}
          >
            {children}
          </div>
          {/* Bottom */}
          <div
            ref={bottomAnchorRef}
            className={clsx('jarvis-ScrollView__bottomAnchor')}
          >
            {_enableOnScrollToBottom && <ScrollToBottomLoading />}
          </div>
        </div>
      </PullToRefresh>
    );
  },
);

export default ScrollView;
